import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createApplicationFormUpdateHandler } from '../../../../components/ApplicationInvite';
import CountrySelectService from '../../../../components/CSC/CountrySelect/CountrySelectService';
import { DialogContext } from '../../../../store/context/DialogContext';
import { useTranslation } from '../../../../store/context/TranslationContext';
import { doGET, doPUT } from '../../../../util/HttpUtil';
import { handleUtmFormSubmit } from '../../../../util/utm';
import pincodeService from '../../components/PincodeField/PincodeService';
import { withComputedHidePinCode } from '../../components/PincodeField/helper';
import ExtraQuestions from '../ExtraQuestions';
import { hasActiveAsks, hasPendingAsks } from '../askIdentity';
import { areTransactionValuesSame, checkIsAskForInfo, convertArrayToDict, fetchIndividualLiteData, FORM_TYPE, FORM_TYPE_LABELS, formatActualTransactions, getIPlusWizardWhenRedo, getSelfdeclaredValues, IPLUS_WIZARD, IPLUS_WIZARD_NUM, isObjectNewTransactionsEmpty, scrollToTop, STEP_NUM, STEP_STATUS, STEPS, TRANSACTION_FIELDS } from '../IPlushelper';
import { getAccountActivityRuleErrors } from '../../../../components/InputField/helper';
import ApplicationDocRedo from '../components/IndividualLite/ApplicationDocRedo';
import FormDocuments from './FormDocuments';
import "../styles.scss";
// import { default as FORMJSON, default as JSON } from "../../../../IndividualPlus.json";
import { AskMoreMessage, ThankYou } from '../components';
import AutoSaveIndicator from '../../../../components/AutoSaveIndicator/AutoSaveIndicator';
import ReviewShellBoundary from '../../../../components/ReviewWizard/ReviewShellBoundary';
import IPlusUserDetailForm from "./IPlusUserDetailForm";
import IPlusDeclarationForm from "./IPlusDeclarationForm";
import KYCVerification from './KYCVerification';
import FormChangeConfirmation from '../../../../components/FormChangeConfirmation';
import useAutoSave from '../../../../hooks/useAutoSave';
import useFormReviewShell from '../../../../hooks/useFormReviewShell';
import useUnsavedChangesWarning from '../../../../hooks/useUnsavedChangesWarning';
import EnhancedDueDiligenceContent from '../../../EDD/EDDContent';
import FormContainer, { FORM_CONTAINER_TYPE } from './FormContainer';
import IPlusAboutYou from "./IPlusAboutYou";
import IPlusDigialAssestsCustody from "./IPlusDigialAssestsCustody";
import IPlusAccountActivityDeclaration from "./IPlusAccountActivityDeclaration";
import ConsoleAccordion from '../BPrime/components/ConsoleAccordion';
import { CONVERSIONS_FIELD_MAP } from './constant';
import IPlusOccupationDetails from './IPlusOccuptationDetails';
import { createIndividualEddAutoSaveAdapter } from '../autosave/buildIndividualEddAutoSavePayload';
import { buildPostAutoSaveUpdatePayload, createAutoSaveFieldDefinitionMap, getAutoSaveProceedBlockedMessage, isAutoSaveEnabledForConfig, resolveAutoSaveDebounceMs } from '../autosave/shared';
import { SUBMIT_DOC_FLOW, validateSubmitDocuments } from '../submitDocumentValidation';

/** * Defining Component mapping for showing selected component based on the step wizard. */
const COMPONENTS = {
    IPlusUserDetailForm,
    IPlusDeclarationForm,
    IPlusOccupationDetails,
    IPlusAboutYou,
    IPlusAccountActivityDeclaration,
    IPlusDigialAssestsCustody,
    ProofOfAddress: FormDocuments,
    KYCVerification,
    ExtraQuestions,
    ApplicationDocRedo,
    ThankYou,
}

const ApplyIndividualPlus = ({ asLabel, formId, editMode = false, refreshId }) => {
    const IS_EDD = true;

    let { individual_id } = useParams();
    let { showError, showConfirm } = useContext(DialogContext);
    const { t } = useTranslation();
    // ====================================================================================================================
    // ============================================= States for the component =============================================
    // ====================================================================================================================
    const [data, setData] = useState({});
    const [applicationForm, setApplicationForm] = useState({});
    const [ondatoStatus, setOndatoStatus] = useState({});
    const [isAskForInfo, setIsAskForInfo] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [editId, setEditID] = useState(individual_id ?? formId);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [step, setStep] = useState({ wizard: IPLUS_WIZARD.IPlusUserDetailForm });
    const [errors, setErrors] = useState({});
    const [applicationSteps, setApplicationSteps] = useState({});
    const [polling, setPolling] = useState(null);
    const [cyclosFields, setCyclosFields] = useState({});
    const [iPlusFields, setIPlusFields] = useState([]);
    const [autoSaveConfig, setAutoSaveConfig] = useState(null);
    const [isFormEditable, setIsFormEditable] = useState(false);
    const [isUserReady, setIsUseready] = useState(false);
    const pageNumberRef = useRef(1);
    const [actualTransactions, setActualTransactions] = useState({});
    const [isKycRequired, setIsKycRequired] = useState(false);
    const {
        reviewError,
        reviewLoading,
        reviewNavigation,
    } = useFormReviewShell({
        applicationId: editId,
        autoLoad: !asLabel && !editMode && Boolean(editId),
        fieldDefinitions: cyclosFields,
        formData: data,
        currentWizard: step?.wizard,
        onNavigateToWizard: (wizard) => {
            setStep({ wizard });
            pageNumberRef.current = IPLUS_WIZARD_NUM[wizard] ?? pageNumberRef.current;
            scrollToTop();
        },
        onPersistError: showError,
    });
    // ====================================================================================================================
    // ============================================ Handler functions for form ============================================
    // ====================================================================================================================
    /** * data change handler */
    const handleChange = (value) => {
        setData(value)
    };

    /** * Getting and setting JSON fields. */
    const getFields = async (group) => {
        try {
            if (Object.keys(pincodeService?.records)?.length === 0) {
                pincodeService.fetch().catch(console.error);
            }
            await CountrySelectService.fetch();
            // const cyclosResponse = { status: 200, data: FORMJSON }; // TODO: get the data from json
            const cyclosResponse = await doGET(`/api/cyclos/fields-json?group=${group}`);
            if (cyclosResponse.status === 200) {
                setAutoSaveConfig(cyclosResponse?.data?.autoSave || null);
                setIPlusFields(cyclosResponse?.data?.fields);
                const customFieldObject = createAutoSaveFieldDefinitionMap(cyclosResponse?.data?.fields);
                setCyclosFields(customFieldObject);
            }
        } catch (error) {
            showError(error);
        }
    };

    useEffect(() => {
        getFields("INDIVIDUAL_EDD");
        CountrySelectService.fetchCyclosCountryMaster();
    }, []);

    useEffect(() => { if (formId) setEditID(formId) }, [formId]);

    const handleUserReady = () => {
        setIsUseready(true);
        scrollToTop();
    }

    /** * Handler function for back button click */
    const handlePrev = (v) => {
        const isFormTypeIPrime = applicationForm?.formType === FORM_TYPE.IndividualPrime;
        const isDigitalAssetsCustody = (data?.whyRequirePrimeAccount || [])?.includes("Higher_personal_savings_investing_or_digital_asset_custody");
        switch (step?.wizard) {
            case IPLUS_WIZARD.IPlusUserDetailForm:
                return
            case IPLUS_WIZARD.IPlusDeclarationForm:
                setStep({ wizard: IPLUS_WIZARD.IPlusUserDetailForm });
                return
            case IPLUS_WIZARD.IPlusOccupationDetails:
                setStep({ wizard: IPLUS_WIZARD.IPlusDeclarationForm });
                return
            case IPLUS_WIZARD.IPlusAboutYou:
                setStep({ wizard: IPLUS_WIZARD.IPlusOccupationDetails });
                return
            case IPLUS_WIZARD.IPlusAccountActivityDeclaration:
                setStep({ wizard: isFormTypeIPrime ? IPLUS_WIZARD.IPlusAboutYou : IPLUS_WIZARD.IPlusDeclarationForm });
                return
            case IPLUS_WIZARD.IPlusDigialAssestsCustody:
                setStep({ wizard: IPLUS_WIZARD.IPlusAccountActivityDeclaration });
                return
            case IPLUS_WIZARD.Documents:
                setStep({ wizard: (isFormTypeIPrime && isDigitalAssetsCustody) ? IPLUS_WIZARD.IPlusDigialAssestsCustody : IPLUS_WIZARD.IPlusAccountActivityDeclaration });
                return
            default:
                return;
        }
    };

    /** * Handler function for skipping the step (user dont want to change any information) click */
    const getNextStep = (wizard) => {
        const isFormTypeIPrime = applicationForm?.formType === FORM_TYPE.IndividualPrime;
        const isDigitalAssetsCustody = (data?.whyRequirePrimeAccount || [])?.includes("Higher_personal_savings_investing_or_digital_asset_custody");
        switch (wizard) {
            case IPLUS_WIZARD.IPlusUserDetailForm:
                return { wizard: IPLUS_WIZARD.IPlusDeclarationForm };
            case IPLUS_WIZARD.IPlusDeclarationForm:
                return { wizard: isFormTypeIPrime ? IPLUS_WIZARD.IPlusOccupationDetails : IPLUS_WIZARD.IPlusAccountActivityDeclaration };
            case IPLUS_WIZARD.IPlusOccupationDetails:
                return { wizard: IPLUS_WIZARD.IPlusAboutYou };
            case IPLUS_WIZARD.IPlusAboutYou:
                return { wizard: IPLUS_WIZARD.IPlusAccountActivityDeclaration };
            case IPLUS_WIZARD.IPlusAccountActivityDeclaration:
                return { wizard: (isFormTypeIPrime && isDigitalAssetsCustody) ? IPLUS_WIZARD.IPlusDigialAssestsCustody : IPLUS_WIZARD.Documents };
            case IPLUS_WIZARD.IPlusDigialAssestsCustody:
                return { wizard: IPLUS_WIZARD.Documents };
            default:
                return { wizard: IPLUS_WIZARD.IPlusAboutYou };
        }
    }

    /* Fetch Actual Transactions */
    const fetchActualTransactions = async (cyclosUserId) => {
        if (!cyclosUserId) return;
        try {
            const actualTransactionsResponse = await doGET(`/api/cyclos/txn-data?cyclosUserId=${cyclosUserId}&application_id=${individual_id}`);
            setActualTransactions(formatActualTransactions(actualTransactionsResponse?.data));
        } catch (error) {
            console.log(error);
        }
    }

    useEffect(() => {
        if (applicationForm?.cyclosUserId)
            fetchActualTransactions(applicationForm?.cyclosUserId);
    }, [applicationForm?.cyclosUserId]);

    useEffect(() => {
        if (reviewNavigation?.hasActionableTasks) {
            setIsUseready(true);
            setIsFormEditable(true);
        }
    }, [reviewNavigation?.hasActionableTasks]);

    const checkOndatoStatusAndRedirect = async (data) => {
        let isKycVerificationRequired = false;
        try {
            const response = await doGET(`/api/individual-lite/edd-info?id=${editId}`);
            if (response.status === 200) {
                const uboIdsForKYC = response?.data?.uboIdsForKYC;
                if (Array.isArray(uboIdsForKYC)) {
                    if (uboIdsForKYC.includes(data?.ubo_id)) {
                        isKycVerificationRequired = true;
                    } else {
                        isKycVerificationRequired = false;
                    }
                } else {
                    isKycVerificationRequired = false;
                }
            }
        } catch (error) {
            showError("Error", error)
        }

        setIsKycRequired(isKycVerificationRequired);
        if (isKycVerificationRequired) {
            try {
                const ondatoGridResponse = await doGET(`/api/ondato/grid?form_id=${data?._id}&ubo_id=${data?.ubo_id}&sortBy=seq&sortAsc=false&rows=-1`);
                if (ondatoGridResponse?.status === 200) {
                    if (ondatoGridResponse?.data?.rows?.length > 0) {
                        const ondatoResponse = await doGET(`/api/ondato/form/status?id=${individual_id}&refresh=true`);
                        setOndatoStatus(ondatoResponse?.data);
                        if (ondatoResponse?.data?.status === "Completed") {
                            setStep({ wizard: IPLUS_WIZARD.ThankYou })
                        } else {
                            setStep({ wizard: IPLUS_WIZARD.KYCVerification })
                        }
                    } else if (ondatoGridResponse?.data?.rows?.length === 0) {
                        setStep({ wizard: IPLUS_WIZARD.KYCVerification })
                    }
                }
            } catch (error) {

            }
        }
    };


    /** * Fetching the for details */
    const fetchDetail = async ({ stepProp, usernameProp, syncUi = true, includeMeta = false } = {}) => {
        let individualLiteResponse = {}
        let applicationFormResponse = {}
        if (syncUi) {
            setDataLoading(true)
        }
        try {
            if (editId) {
                localStorage.setItem("formId", editId);
                /** * Fetching the detail of individual-lite */
                individualLiteResponse = await fetchIndividualLiteData(editId);
                /** * Fetching the details of application */
                const applicationResponse = await doGET(`/api/application-form/detail?id=${editId}&withExtension=true`)
                if (applicationResponse?.status == 200) {
                    applicationFormResponse = applicationResponse?.data
                    if (syncUi && applicationFormResponse?.steps?.length) {

                        /** * checking if any setp has status 2 (Incomplete) */
                        const askInfoStep = checkIsAskForInfo(applicationFormResponse?.steps)

                        /** * Formatting the steps into object for easy data reading */
                        const stepsObj = convertArrayToDict(applicationFormResponse?.steps)
                        setApplicationSteps(stepsObj);

                        /** * if both form and documents are in submit, approved or rejected state then show thank you (nothing for user to do) */
                        if (stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status > 0
                            && [1, 3, 4]?.includes(stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status)
                            && stepsObj[STEPS.APPLICATION_FORM]?.status > 0
                            && [1, 3, 4]?.includes(stepsObj[STEPS.APPLICATION_FORM]?.status)
                        ) {
                            setStep({ wizard: IPLUS_WIZARD.ThankYou });
                        }

                        const applicationFormStepStatus = stepsObj[STEPS.APPLICATION_FORM]?.status;
                        const documentVerificationStepStatus = stepsObj[STEPS.DOCUMENT_VERIFICATION]?.status;
                        const isReadyForKycStage = [applicationFormStepStatus, documentVerificationStepStatus]
                            .every((status) => status > STEP_STATUS.DRAFT && status !== STEP_STATUS.INCOMPLETE);

                        /** * if both form and documents are not in draft/incomplete then let edd-info decide whether KYC is required */
                        if (isReadyForKycStage) {
                            await checkOndatoStatusAndRedirect(individualLiteResponse);
                        }

                        /** * if any step has ask for info then we do application-redo */
                        if (stepsObj[STEPS.APPLICATION_FORM]?.status > 0 && Object.keys(askInfoStep ?? {}).length) {
                            const askInfoStepData = { ...(askInfoStep ?? {}), isAskForInfo: true, step: getIPlusWizardWhenRedo(askInfoStep?.label), }
                            /** * will take decision based in IsAskForInfo object */
                            setIsAskForInfo(askInfoStepData)
                            /** * setting step to thank you as we are rendering using IsAskForInfo */
                            setStep({ wizard: IPLUS_WIZARD.ThankYou })
                        } else {
                            setIsAskForInfo(null)
                        }
                    }

                    /** * checking if we have to hide or show pincode field based in country
                     * (also self-heals a missing pinCode when the country has a fixed default) */
                    individualLiteResponse.address = withComputedHidePinCode(individualLiteResponse.address);

                    /** * application-form data */
                    setApplicationForm(applicationResponse?.data)
                    /** * individual-lite */
                    const formData = {
                        ...individualLiteResponse,
                        cyclosUserName: usernameProp ? usernameProp : applicationResponse?.data?.cyclosUserName,
                        selfDeclaredValues: getSelfdeclaredValues(individualLiteResponse),
                        formType: applicationResponse?.data?.formType
                    };
                    if (syncUi) {
                        setData(formData)
                    }
                    if (includeMeta) {
                        return {
                            formData,
                            applicationFormData: applicationResponse?.data,
                        };
                    }
                    return formData;
                }
            }
        } catch (error) {
            // showError(error)
        } finally {
            if (syncUi) {
                try {
                    /** * checking ondato status if KYC is completed or not if the user reloads the page */
                    if (editId
                        && (applicationFormResponse?.steps ?? [])?.find(v => v?.label == STEPS.APPLICATION_FORM)?.status != 0) {
                        const ondatoResponse = await doGET(`/api/ondato/form/status?id=${editId}&refresh=true`)
                        if (ondatoResponse?.data) {
                            setOndatoStatus(ondatoResponse?.data)
                        }
                    }
                } catch (error) {
                } finally {
                    setDataLoading(false)
                    if (stepProp) setStep(stepProp)
                    scrollToTop()
                }
            }
        }
        return null;
    }

    // JSON owns wizard gating; these extra guards stay page-side because they depend on
    // runtime workflow status and editability rather than static field config.
    const isAutoSaveEnabled = !asLabel
        && !!editId
        && !!data?._id
        && !loading
        && !dataLoading
        && isUserReady
        && isFormEditable
        && ![1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status)
        && isAutoSaveEnabledForConfig({
            autoSaveConfig,
            step: step?.wizard,
        });
    const autoSaveDebounceMs = resolveAutoSaveDebounceMs(autoSaveConfig);

    const autoSaveAdapter = useMemo(
        () => createIndividualEddAutoSaveAdapter(applicationForm, cyclosFields, autoSaveConfig),
        [applicationForm, autoSaveConfig, cyclosFields]
    );

    const syncAutoSaveVersion = useCallback((nextVersion, savedId, recordKey = 'primary') => {
        if (nextVersion === null || nextVersion === undefined || !savedId) return;

        if (recordKey === 'applicationForm') {
            setApplicationForm((prev) => {
                if (!prev?._id || prev._id !== savedId) {
                    return prev;
                }

                return {
                    ...prev,
                    version: nextVersion,
                    cyclosUserName: data?.cyclosUserName ?? prev?.cyclosUserName,
                };
            });
        }
    }, [data?.cyclosUserName]);

    const {
        autoSaveStatus,
        lastSavedAt,
        pendingChangesCount,
        pendingChanges,
        hasUnsavedChanges,
        isAutoSaveNavigationPending,
        flushAndPauseAutoSave,
        resumeAutoSave,
    } = useAutoSave({
        data,
        isEnabled: isAutoSaveEnabled,
        debounceMs: autoSaveDebounceMs,
        adapter: autoSaveAdapter,
        onDirtyUpdate: async () => {
            const result = await fetchDetail({ syncUi: false, includeMeta: true });
            if (!result?.formData) {
                return null;
            }

            return {
                data: result.formData,
                relatedRecords: result?.applicationFormData ? [{
                    recordKey: 'applicationForm',
                    data: result.applicationFormData,
                }] : [],
            };
        },
        onDataReconcile: setData,
        onFieldPersisted: reviewNavigation?.commitFieldTask,
        onVersionIncrement: syncAutoSaveVersion,
    });

    useUnsavedChangesWarning({
        when: hasUnsavedChanges || loading,
        isSaving: autoSaveStatus === 'saving' || autoSaveStatus === 'resyncing',
        pendingChangesCount,
    });

    const nextActionLoading = loading || isAutoSaveNavigationPending;
    const currentPageReviewTasks = reviewNavigation?.currentWizardActionableTasks || [];
    const hasCurrentWizardVisibleReviewTasks = Boolean(reviewNavigation?.currentWizardVisibleTasks?.length);

    useEffect(() => {
        fetchDetail({});
    }, [editId, refreshId]);

    const handleUpdate = async ({ nextStep, payload, isfetchDetail, isLastStep, skipUpdate }) => {
        let errorFlag = false;
        let latestData = data;
        let didResumeAutoSave = false;
        if ((payload ?? data)?._id) {
            setLoading(true)
            setHasError(false);
            const autoSaveResult = await flushAndPauseAutoSave();
            if (!autoSaveResult?.ok) {
                setLoading(false);
                setHasError(true);
                showError(getAutoSaveProceedBlockedMessage(autoSaveResult?.reason));
                return null;
            }
            latestData = autoSaveResult?.latestData || data;
            const refreshedCurrentPageReviewTasks = reviewNavigation?.peekCurrentWizardActionableTasks?.()
                || currentPageReviewTasks;
            const refreshedActionableReviewTasks = reviewNavigation?.peekActionableTasks?.()
                || reviewNavigation?.actionableTasks
                || [];
            if (nextStep && !isLastStep && !asLabel && !editMode && refreshedCurrentPageReviewTasks.length) {
                const blockedCurrentPageTask = reviewNavigation?.peekPreferredActionableTask?.(reviewNavigation?.currentWizard || "")
                    || refreshedCurrentPageReviewTasks[0];
                resumeAutoSave(latestData);
                didResumeAutoSave = true;
                setLoading(false);
                showError("Please finish the remaining items on this page before continuing.");
                if (blockedCurrentPageTask) {
                    reviewNavigation?.goToTask(blockedCurrentPageTask);
                }
                return { status: 400 };
            }
            if (isLastStep && !asLabel && !editMode && refreshedActionableReviewTasks.length) {
                const blockedActionableTask = reviewNavigation?.peekPreferredActionableTask?.()
                    || refreshedActionableReviewTasks[0];
                resumeAutoSave(latestData);
                didResumeAutoSave = true;
                setLoading(false);
                showError("Please complete all requested items before submitting.");
                if (blockedActionableTask) {
                    reviewNavigation?.goToTask(blockedActionableTask);
                }
                return { status: 400 };
            }
            let updatedPayload = buildPostAutoSaveUpdatePayload(latestData, payload);
            let submissionData = {
                ...latestData,
                formType: applicationForm?.formType,
            };

            if (step.wizard === IPLUS_WIZARD.IPlusAccountActivityDeclaration) {
                const { success, fieldsToUpdate, error } = await askForConversionValueUpdate();
                if (!success) {
                    setLoading(false);
                    showError(error);
                    return;
                }
                fieldsToUpdate.forEach(field => {
                    updatedPayload[field] = true;
                });
            }
            /** * deleting empty strings attributes */
            if ('middleName' in updatedPayload && updatedPayload.middleName === "") {
                delete updatedPayload.middleName;
            }
            if ('taxId' in updatedPayload && updatedPayload.taxId === "") {
                delete updatedPayload.taxId;
            }
            if ('ssn' in updatedPayload && updatedPayload.ssn === "") {
                delete updatedPayload.ssn;
            }

            // for wizard === IPlusAccountActivityDeclaration (Table component), i want to set  the default declared value in new necleration of user has no changes.
            // if (step.wizard === IPLUS_WIZARD.IPlusAccountActivityDeclaration) {
            //     const selfDeclared = data?.selfDeclaredValues ?? {};
            //     for (const key in selfDeclared) {
            //         if (
            //             !(key in updatedPayload) ||
            //             !updatedPayload[key]
            //         ) {
            //             updatedPayload[key] = selfDeclared[key];
            //         }
            //     }
            // }

            delete updatedPayload?.selfDeclaredValues;

            try {
                /** * if not asLabel or not skip update then go inside this block */
                if (!asLabel && !skipUpdate) {
                    /** * update API called */
                    const response = await doPUT(`/api/individual-lite/update`, updatedPayload)
                    if (response?.status == 200) {
                        const iPlusResponse = response?.data ?? {};

                        /** * checking if we have to hide or show pincode field based in country
                         * (also self-heals a missing pinCode when the country has a fixed default) */
                        iPlusResponse.address = withComputedHidePinCode(iPlusResponse.address);

                        /** 
                         * my user name is saved in application-form object
                         * so when we go from first page to second (IPlusDeclarationForm) then we save the user name in application-form
                         * fetching the fresh application-form object with new fresh data
                         */
                        if (nextStep?.wizard === "IPlusDeclarationForm") {
                            const applicationFormData = await doGET(`/api/application-form/detail?id=${editId}`)
                            if (applicationFormData?.status == 200) {
                                const updatedpplicationFormData = await doPUT(`/api/application-form/update`, {
                                    ...applicationFormData?.data,
                                    cyclosUserName: latestData?.cyclosUserName
                                })
                                if (updatedpplicationFormData?.status == 200) {
                                    const nextData = {
                                        ...iPlusResponse,
                                        cyclosUserName: updatedpplicationFormData?.data?.cyclosUserName,
                                        selfDeclaredValues: getSelfdeclaredValues(iPlusResponse),
                                        formType: applicationFormData?.data?.formType
                                    };
                                    setData(nextData);
                                    submissionData = nextData;
                                    resumeAutoSave(nextData);
                                    didResumeAutoSave = true;
                                }
                                else {
                                    const nextData = {
                                        ...iPlusResponse,
                                        cyclosUserName: latestData?.cyclosUserName,
                                        selfDeclaredValues: getSelfdeclaredValues(iPlusResponse),
                                        formType: applicationForm?.formType,
                                    };
                                    setData(nextData);
                                    submissionData = nextData;
                                    resumeAutoSave(nextData);
                                    didResumeAutoSave = true;
                                }
                            }
                            else {
                                const nextData = {
                                    ...iPlusResponse,
                                    cyclosUserName: latestData?.cyclosUserName,
                                    selfDeclaredValues: getSelfdeclaredValues(iPlusResponse),
                                    formType: applicationForm?.formType,
                                };
                                setData(nextData);
                                submissionData = nextData;
                                resumeAutoSave(nextData);
                                didResumeAutoSave = true;
                            }
                        } else { /** setting cyclosUserName in state */
                            const nextData = {
                                ...iPlusResponse,
                                cyclosUserName: latestData?.cyclosUserName,
                                selfDeclaredValues: getSelfdeclaredValues(iPlusResponse),
                                formType: applicationForm?.formType,
                            };
                            setData(nextData);
                            submissionData = nextData;
                            resumeAutoSave(nextData);
                            didResumeAutoSave = true;
                        }
                    } else {
                        errorFlag = true
                        resumeAutoSave(null);
                        didResumeAutoSave = true;
                    }
                }

                /** * if isLastStep and not asLabel then submit the form */
                if (isLastStep && !asLabel) {
                    const documentValidation = await validateSubmitDocuments({
                        flow: SUBMIT_DOC_FLOW.INDIVIDUAL_PERIODIC_REVIEW,
                        recordId: editId,
                        data: submissionData,
                        applicationForm,
                        formType: applicationForm?.formType,
                    });

                    if (!documentValidation?.isValid) {
                        setHasError(true);
                        showError(documentValidation?.errors?.[0]?.error || "Please upload all required documents before submitting.");
                        errorFlag = true;
                        return { status: 400 };
                    }

                    const submitResponse = await doGET(`/api/individual-lite/submit?id=${editId}&markFormSubmit=true&markDocSubmit=true`);
                    handleUtmFormSubmit({
                        formType: "INDIVIDUAL_PLUS",
                        utmPayload: {
                            utm_source: applicationForm?.extension?.utm_source,
                            utm_medium: applicationForm?.extension?.utm_medium,
                            utm_campaign: applicationForm?.extension?.utm_campaign,
                            utm_content: applicationForm?.extension?.utm_content,
                            utm_term: applicationForm?.extension?.utm_term,
                        }
                    })
                    if (submitResponse?.status == 200) {
                        await reviewNavigation?.markVisibleTasksResponded?.({ quiet: true });
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        /** if there is next step then go to it */
                        if (isfetchDetail) {
                            pageNumberRef.current = 1;
                            await fetchDetail(nextStep ? { stepProp: nextStep } : {});
                            setLoading(false);
                        }
                    } else {
                        errorFlag = true;
                    }
                } else {
                    /** go to next step / page */
                    if (nextStep) setStep(nextStep)
                    pageNumberRef.current = pageNumberRef.current + 1;
                    scrollToTop()
                    setLoading(false);
                }
            } catch (error) {
                setHasError(true);
                showError(error)
                errorFlag = true
                resumeAutoSave(null);
                didResumeAutoSave = true;
            }
            finally {
                if (skipUpdate && !didResumeAutoSave) {
                    resumeAutoSave(null);
                }
                setLoading(false)
            }
        } else {
            /** * if not id present then just simply go to next step */
            if (nextStep) fetchDetail({ stepProp: nextStep })
            resumeAutoSave(null);
        }

        return errorFlag ? { status: 400 } : { status: 200 }
    };

    // ====================================================================================================================
    // =============================================== Conversions Handlers ===============================================
    // ====================================================================================================================

    const askForConversionValueUpdate = async () => {
        const fieldsToUpdate = [];
        if (
            data?.hasCoinConversionPlan === false && (
                data?.Number_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
                data?.Amount_Of_Monthly_Stablecoin_Conversions_Deposits > 0 ||
                data?.Number_Of_Monthly_Stablecoin_Conversions_Payments > 0 ||
                data?.Amount_Of_Monthly_Stablecoin_Conversions_Payments > 0
            )) {
            fieldsToUpdate.push("hasCoinConversionPlan");
        }
        if (
            data?.hasCryptoConversionPlan === false && (
                data?.Count_Of_Monthly_Cryptocurrency_Conversions > 0 || data?.Amount_Of_Monthly_Cryptocurrency_Conversions > 0
            )) {
            fieldsToUpdate.push("hasCryptoConversionPlan");
        }
        if (fieldsToUpdate.length === 0) {
            return { success: true, fieldsToUpdate: [], error: "" };
        } else {
            const title = fieldsToUpdate.map(field => CONVERSIONS_FIELD_MAP[field]).join(" and ");
            const response = await showConfirm({
                title: t(`Do you want to update ${title} to Yes?`),
            });
            if (response) return { success: true, fieldsToUpdate, error: '' };
            else return { success: false, fieldsToUpdate, error: `You have to update ${title} to Yes or change their values to 0 to proceed.` };
        }
    }

    // ====================================================================================================================
    // =========================================== Handler functions for Banner ===========================================
    // ====================================================================================================================
    /** * To show or hide the bottom banner asking user to change any information */
    useEffect(() => {
        if (reviewNavigation?.hasActionableTasks || hasCurrentWizardVisibleReviewTasks) {
            setIsFormEditable(true);
            return;
        }

        /** If the wizard page is of form type first 5-6 pages then we show banner */
        if (![
            IPLUS_WIZARD.EnhancedDueDiligenceContent,
            IPLUS_WIZARD.ExtraQuestions,
            IPLUS_WIZARD.ApplicationDocRedo,
            IPLUS_WIZARD.Documents,
            IPLUS_WIZARD.KYCVerification,
        ].includes(step?.wizard)) {
            /** 
             * But if the wizard is IPlusAccountActivityDeclaration (transaction table) then: 
             * 1. If the transaction values are same as self declared values then we show banner
             * 2. If all self declared values are present and transaction values are empty then we show banner
             * else we hide the banner and make form editable
            * */
            if (step.wizard === IPLUS_WIZARD.IPlusAccountActivityDeclaration) {
                if (areTransactionValuesSame(data, data?.selfDeclaredValues)) {
                    setIsFormEditable(false);
                } else if (
                    (Object.keys(data?.selfDeclaredValues).length === TRANSACTION_FIELDS.length) &&
                    isObjectNewTransactionsEmpty(data)
                ) {
                    setIsFormEditable(false);
                } else {
                    setIsFormEditable(true);
                }
            } else {
                setIsFormEditable(false);
            }
        } else {
            setIsFormEditable(true);
        }
    }, [hasCurrentWizardVisibleReviewTasks, reviewNavigation?.hasActionableTasks, step?.wizard])

    /**
     * If any field is required then we do not have to ask for any changes as the user needs to fill the required field to proceed.
     * If this is not done then user may bypass the required field.
    */
    useEffect(() => {
        for (const field of iPlusFields) {
            const fieldError = _.get(errors, field.field);
            if (fieldError && field?.wizard !== IPLUS_WIZARD.IPlusAccountActivityDeclaration) { /** If any required field is empty */
                setIsFormEditable(true);
                return;
            }
        }
    }, [errors]);

    const bannerConfig = useMemo(() => {
        const isActivityDeclaration = step.wizard === IPLUS_WIZARD.IPlusAccountActivityDeclaration;
        return {
            title: isActivityDeclaration
                ? ""
                : "Please confirm if any of the previously provided information has changed?",

            noButtonText: isActivityDeclaration
                ? "No changes required"
                : "No changes",

            yesButtonText: isActivityDeclaration
                ? "I want to update my expected activity"
                : "Edit",

            btnWidth: isActivityDeclaration
                ? "350px"
                : "180px"
        };

    }, [step.wizard])

    /** * skipping the step without any changes and saving */
    const handleSkipChanges = async ({ nextStep }) => {
        /* for IPlusAccountActivityDeclaration (transaction table) wizard we clone the self declared values when we skip and remove clear the transaction fields from diffMap */
        if (step.wizard === IPLUS_WIZARD.IPlusAccountActivityDeclaration) {
            const updatedDiffMap = { ...data?.diffMap || {} };
            TRANSACTION_FIELDS.forEach(field => {
                delete updatedDiffMap[field];
            });
            const updatedData = {
                ...data,
                ...data?.selfDeclaredValues,
                diffMap: updatedDiffMap,
            };
            setData(updatedData);

            const crossFieldErrors = getAccountActivityRuleErrors(updatedData, {
                depositLocationFields: ["depositLocations"],
                paymentLocationFields: ["paymentLocations"]
            });
            if (crossFieldErrors.length) {
                setHasError(true);
                setIsFormEditable(true);
                const firstError = crossFieldErrors[0];
                showError(firstError.message);
                const mapped = crossFieldErrors.reduce((acc, err) => {
                    const fields = Array.isArray(err.activityFields) && err.activityFields.length ? err.activityFields : [err.field];
                    fields.forEach(field => _.set(acc, field, err.message));
                    return acc;
                }, {});
                setErrors((prev) => _.merge({}, prev, mapped));
                return;
            }

            // Handle update
            const isDigitalAssetsCustody = (updatedData?.whyRequirePrimeAccount || [])?.includes("Higher_personal_savings_investing_or_digital_asset_custody");
            await handleUpdate({ nextStep: { wizard: isDigitalAssetsCustody ? IPLUS_WIZARD.IPlusDigialAssestsCustody : IPLUS_WIZARD.Documents }, payload: updatedData });
        }
        else {
            if (nextStep) {
                setStep(nextStep)
                scrollToTop()
            }
        }
    };

    const handleAcceptEditChanges = (e) => {
        e?.preventDefault();
        setIsFormEditable(true);
        /* for IPlusAccountActivityDeclaration (transaction table) if we require to change then we clear the filled transaction value from data state */
        if (step?.wizard === IPLUS_WIZARD.IPlusAccountActivityDeclaration) {
            setData(prev => {
                const updated = { ...prev }; // shallow copy
                TRANSACTION_FIELDS.forEach(field => {
                    delete updated[field];
                    if (!updated?.selfDeclaredValues[field])
                        delete updated?.diffMap[field];
                    else updated.diffMap[field] = updated?.selfDeclaredValues[field]; // If the self declared value for field is present then set the diffMap value for that field to its selfDeclaredValue
                });
                return updated;
            });
        }
    }

    const isLoading = dataLoading || !Object.keys(cyclosFields)?.length || !CountrySelectService?.cyclosCountryMaster?.length;
    const CurrentComponent = COMPONENTS[step.wizard];
    const hasActionableReviewTasks = Boolean(reviewNavigation?.hasActionableTasks);
    const hasVisibleReviewTasks = Boolean(reviewNavigation?.hasVisibleTasks);
    const hasResolvedNoActionableReviewTasks = !reviewLoading && !hasActionableReviewTasks;
    const applicationFormStepStatus = applicationSteps[STEPS.APPLICATION_FORM]?.status;
    const documentVerificationStepStatus = applicationSteps[STEPS.DOCUMENT_VERIFICATION]?.status;
    const isDocumentRedoRequired = applicationFormStepStatus > STEP_STATUS.DRAFT
        && [STEP_STATUS.DRAFT, STEP_STATUS.INCOMPLETE].includes(documentVerificationStepStatus);

    const renderInFormLayout = (content, showReviewWizard = false) => (
        <FormContainer
            asLabel={asLabel}
            editMode={editMode}
            form_id={data?._id}
            formContainerType={FORM_CONTAINER_TYPE.EDD}
            title={IS_EDD ? `Periodic Review - ${FORM_TYPE_LABELS[applicationForm?.formType]}` : `Individual Plus`}
            isLoading={isLoading}
            applicationForm={applicationForm}
            applicationFormId={editId}
            formData={data}
            onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
        >
            <div className='form-content-body-content flex-1'>
                <div className='d-flex justify-content-end mb-3'>
                    <AutoSaveIndicator
                        status={autoSaveStatus}
                        lastSavedAt={lastSavedAt}
                        pendingChangesCount={pendingChangesCount}
                        pendingChanges={pendingChanges}
                    />
                </div>
                <ReviewShellBoundary
                    reviewNavigation={reviewNavigation}
                    loading={reviewLoading}
                    error={reviewError}
                    showReviewWizard={showReviewWizard}
                >
                    {content}
                </ReviewShellBoundary>
            </div>
        </FormContainer>
    );


    // ====================================================================================================================
    // ================================================= Component Rendering ==============================================
    // ====================================================================================================================
    /** document-redo after submitting application */
    if (!asLabel && !isLoading) {
        if (isDocumentRedoRequired) {
            if (polling) clearInterval(polling);
            return renderInFormLayout(
                <ApplicationDocRedo
                    hideContainerStyle
                    recordId={editId}
                    asLabel={asLabel}
                    formId={editId}
                    formType={"INDIVIDUAL_PLUS"}
                    type={IS_EDD ? `Periodic Review - ${FORM_TYPE_LABELS[applicationForm?.formType]}` : `Individual Plus`}
                />
            );
        }
    }

    /** if we are ahead of last step and my ask for info is incomplete then show questions */
    if (!asLabel && !isLoading) {
        if (IPLUS_WIZARD_NUM[step?.wizard] > (hasPendingAsks(data?.asks) ? STEP_NUM.APPLICATION_FORM_REDO : STEP_NUM.KYC_VERIFICATION)
            && isAskForInfo?.status == STEP_STATUS.INCOMPLETE
            && isAskForInfo?.label == STEPS.APPLICATION_FORM
        ) {
            return renderInFormLayout(
                <AskMoreMessage
                    hideContainerStyle
                    data={data}
                    title={IS_EDD ? `Periodic Review - ${FORM_TYPE_LABELS[applicationForm?.formType]}` : `Individual Plus`}
                    remark={(data?.remark ?? "")}
                    loading={nextActionLoading}
                    onNext={() => { setStep({ wizard: IPLUS_WIZARD.ExtraQuestions }) }}
                />
            );
        }
    }

    /** if the step is greater than application-redo (in case of askForInfo) or KYC then we have no next page so render ThankYou page */
    if (!asLabel && !isLoading) {
        if (IPLUS_WIZARD_NUM[step?.wizard] > (hasPendingAsks(data?.asks) ? STEP_NUM.APPLICATION_FORM_REDO : STEP_NUM.KYC_VERIFICATION)) {
            if (polling) clearInterval(polling);
            return <ThankYou
                application_id={editId}
                type={applicationForm?.formType}
                isForEdd
                isKycRequired={isKycRequired}
                applicationForm={applicationForm}
                formData={data}
            />
        }
    }

    /** rendering the steps as accordian */
    const renderAsAccordion = () => {
        const whyRequirePrimeAccount = data?.whyRequirePrimeAccount || [];
        const isDigitalAssetsCustody = whyRequirePrimeAccount?.includes("Higher_personal_savings_investing_or_digital_asset_custody");

        const commonProps = {
            decrementStep: handlePrev,
            recordId: individual_id,
            cyclosFields,
            iPlusFields,
            data,
            handleChange,
            pageNum: pageNumberRef.current,
            loading: nextActionLoading,
            step,
            wizard: step?.wizard,
            handleUpdate,
            setErrors,
            setData,
            asLabel: !editMode,
            editMode: editMode,
            hasError,
            applicationForm,
            errors,
            setLoading,
            hideButtons: true,
            actualTransactions,
            formType: applicationForm?.formType
        };

        const extraQuestionsProps = {
            dataLoading: loading || dataLoading,
            formId: individual_id,
            formType: applicationForm?.formType,
            onSubmit: async () => {
                await fetchDetail({});
                window.location.reload();
            },
            questionIds: data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? []
        }

        const steps = [
            <IPlusUserDetailForm label="Applicant Information" {...commonProps} isForEDD={true} />,
            <IPlusDeclarationForm label="Declaration" {...commonProps} isForEDD={true} />,
            <IPlusOccupationDetails label="Occupation Details" {...commonProps} isForEDD={true} />,
            <IPlusAboutYou label="About Applicant" {...commonProps} isForEDD={true} />,
            <IPlusAccountActivityDeclaration label="Account Usage Declaration" {...commonProps} isForEDD={true} />,
            (isDigitalAssetsCustody) && <IPlusDigialAssestsCustody label="Digital Assets Custody and Conversion" {...commonProps} isForEDD={true} />,
            hasActiveAsks(data?.asks) && <ExtraQuestions label="Extra Infromation" {...commonProps} {...extraQuestionsProps} />
        ];

        return <ConsoleAccordion steps={steps.filter(Boolean)} />;
    };

    // ====================================================================================================================
    // ================================================== What is EDD Page ================================================
    // ====================================================================================================================
    /** showing content */
    if (
        !isUserReady
        && !isLoading
        && hasResolvedNoActionableReviewTasks
        && IPLUS_WIZARD_NUM[step?.wizard] != STEP_NUM.APPLICATION_FORM_REDO
    ) {
        return (
            <FormContainer
                asLabel={asLabel}
                editMode={editMode}
                form_id={data?._id}
                formContainerType={FORM_CONTAINER_TYPE.EDD}
                title={IS_EDD ? `Periodic Review - ${FORM_TYPE_LABELS[applicationForm?.formType]}` : `Individual Plus`}
                applicationForm={applicationForm}
                applicationFormId={editId}
                formData={data}
                onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
            >
                <EnhancedDueDiligenceContent handleNext={() => handleUserReady()} />
            </FormContainer>
        )
    }

    const currentComponentDisabled = () => {
        /* If the wizard is in first 5-6 pages */
        if ([
            IPLUS_WIZARD.IPlusUserDetailForm,
            IPLUS_WIZARD.IPlusDeclarationForm,
            IPLUS_WIZARD.IPlusAboutYou,
            IPLUS_WIZARD.IPlusOccupationDetails,
            IPLUS_WIZARD.IPlusAccountActivityDeclaration,
            IPLUS_WIZARD.IPlusDigialAssestsCustody,
        ].includes(step?.wizard)) {
            if (hasCurrentWizardVisibleReviewTasks) {
                return asLabel;
            }

            return asLabel || !isFormEditable || [1, 2, 3, 4]?.includes(applicationSteps[STEPS.APPLICATION_FORM]?.status);
        } else {
            return asLabel;
        }
    }

    const getPropsForCurrentComponent = () => {
        const commoProps = {
            asLabel: currentComponentDisabled(),
            hideButtons: asLabel || !isFormEditable,
            data,
            handleUpdate,
            setHasError,
            isForEDD: IS_EDD,
            handlePrev,
            formType: applicationForm?.formType,
        }

        if ([
            IPLUS_WIZARD.IPlusUserDetailForm,
            IPLUS_WIZARD.IPlusDeclarationForm,
            IPLUS_WIZARD.IPlusAboutYou,
            IPLUS_WIZARD.IPlusOccupationDetails,
            IPLUS_WIZARD.IPlusAccountActivityDeclaration,
            IPLUS_WIZARD.IPlusDigialAssestsCustody,
        ].includes(step.wizard)) {
            return {
                ...commoProps,
                hasError,
                loading,
                setLoading,
                cyclosFields,
                errors,
                setErrors,
                iPlusFields,
                wizard: step.wizard,
                handleChange,
                actualTransactions,
                onOtpVerify: () => { fetchDetail({ usernameProp: data?.cyclosUserName }) },
                uiConfig: {
                    showInputColumns: !IS_EDD | isFormEditable || [
                        IPLUS_WIZARD.KYCVerification,
                        IPLUS_WIZARD.EnhancedDueDiligenceContent,
                        IPLUS_WIZARD.ExtraQuestions,
                        IPLUS_WIZARD.ApplicationDocRedo,
                        IPLUS_WIZARD.Documents,
                    ].includes(step?.wizard)
                }
            }
        }
        switch (step.wizard) {
            case IPLUS_WIZARD.Documents:
                return {
                    ...commoProps,
                    recordId: editId,
                    isAskForInfo: isAskForInfo?.status == STEP_STATUS.INCOMPLETE,
                    remark: data?.remark
                }
            case IPLUS_WIZARD.KYCVerification:
                return {
                    recordId: editId,
                    data,
                    applicationForm,
                    asLabel: currentComponentDisabled(),
                    ondatoStatus, setOndatoStatus,
                    loading, setLoading,
                    polling, setPolling,
                    isAskForInfo,
                    setStep,
                    fetchDetail,
                    isForEDD: IS_EDD,
                }
            case IPLUS_WIZARD.ExtraQuestions:
                return {
                    dataLoading: loading || dataLoading,
                    formType: applicationForm?.formType,
                    data, asLabel: currentComponentDisabled(),
                    questionIds: data?.asks?.map((v) => v?.question_id).filter(Boolean) ?? [],
                    formId: editId,
                    onSubmit: async () => {
                        await fetchDetail({})
                        window.location.reload();
                    }
                }
            case IPLUS_WIZARD.ApplicationDocRedo:
                return {
                    type: IS_EDD ? `Periodic Review - ${FORM_TYPE_LABELS[applicationForm?.formType]}` : `Individual Plus`,
                    formType: applicationForm?.formType,
                    formId: editId,
                    hideContainerStyle: true,
                    asLabel: currentComponentDisabled(),
                    recordId: editId
                }
            case IPLUS_WIZARD.ThankYou:
                return {
                    application_id: data?._id,
                    type: applicationForm?.formType,
                    isForEDD: true,
                    isKycRequired: isKycRequired
                }
            default:
                break;
        }
    }

    /** Wrapper container to show componenent based on current wizard */
    return (
        <FormContainer
            asLabel={asLabel}
            editMode={editMode}
            form_id={data?._id}
            formContainerType={FORM_CONTAINER_TYPE.EDD}
            title={IS_EDD ? `Periodic Review - ${FORM_TYPE_LABELS[applicationForm?.formType]}` : `Individual Plus`}
            isLoading={isLoading}
            applicationForm={applicationForm}
            applicationFormId={editId}
            formData={data}
            onApplicationFormUpdated={createApplicationFormUpdateHandler(setApplicationForm)}
        >

            <div className='form-content-body-content flex-1'>
                <div className='d-flex justify-content-end mb-3'>
                    <AutoSaveIndicator status={autoSaveStatus} lastSavedAt={lastSavedAt} pendingChangesCount={pendingChangesCount} pendingChanges={pendingChanges} />
                </div>
                {asLabel ? renderAsAccordion() : (
                    <ReviewShellBoundary
                        reviewNavigation={reviewNavigation}
                        loading={reviewLoading}
                        error={reviewError}
                        showReviewWizard={hasVisibleReviewTasks && ![
                            IPLUS_WIZARD.ExtraQuestions,
                            IPLUS_WIZARD.ApplicationDocRedo,
                            IPLUS_WIZARD.KYCVerification,
                            IPLUS_WIZARD.ThankYou,
                        ].includes(step?.wizard)}
                    >
                        {CurrentComponent ? (
                            <CurrentComponent
                                {...getPropsForCurrentComponent()}
                            />
                        ) : null}
                    </ReviewShellBoundary>
                )}
            </div>

            {/* Confirmation for form changes */}
            {IS_EDD && !isFormEditable && ![
                IPLUS_WIZARD.KYCVerification,
                IPLUS_WIZARD.EnhancedDueDiligenceContent,
                IPLUS_WIZARD.ExtraQuestions,
                IPLUS_WIZARD.ApplicationDocRedo,
                IPLUS_WIZARD.Documents,
            ].includes(step?.wizard) && hasResolvedNoActionableReviewTasks && !hasCurrentWizardVisibleReviewTasks ?
                <FormChangeConfirmation
                    onClickYes={handleAcceptEditChanges}
                    onClickNo={(e) => {
                        e?.preventDefault();
                        handleSkipChanges({ nextStep: getNextStep(step?.wizard) })
                    }}
                    loading={nextActionLoading || dataLoading}
                    config={bannerConfig}
                /> :
                null}
        </FormContainer>
    );
};

export default observer(ApplyIndividualPlus);
